import { FormEvent, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { getApiErrorMessage } from '@/lib/api/errors';
import { verifyPayrollMfa } from '@/lib/api/payrollBridge';
import { cn } from '@/lib/utils';

interface PayrollMfaGateProps {
  className?: string;
  onVerified: () => void;
}

export const PayrollMfaGate = ({ className, onVerified }: PayrollMfaGateProps) => {
  const { t } = useTranslation(['common', 'payrollBridge']);
  const { toast } = useToast();
  const [code, setCode] = useState('');

  const verifyMutation = useMutation({
    mutationFn: (payload: string) => verifyPayrollMfa(payload),
    onSuccess: () => {
      setCode('');
      toast({
        title: t('common:status.success', { defaultValue: 'Success' }),
        description: t('payrollBridge:mfa.success', { defaultValue: 'Payroll access unlocked.' }),
      });
      onVerified();
    },
  });

  const { error, isPending } = verifyMutation;
  const parsedError = error ? getApiErrorMessage(error) ?? null : null;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!code || code.length < 6 || isPending) {
      return;
    }
    verifyMutation.mutate(code);
  };

  return (
    <Card
      className={cn(
        'border border-emerald-200 bg-emerald-50/80 shadow-sm dark:border-emerald-500/40 dark:bg-emerald-500/10',
        className,
      )}
    >
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-emerald-900 dark:text-emerald-100">
          {t('payrollBridge:mfa.title', { defaultValue: 'Verify payroll access' })}
        </CardTitle>
        <CardDescription className="text-sm text-emerald-900 dark:text-emerald-100">
          {t('payrollBridge:mfa.description', {
            defaultValue: 'Enter the verification code from your authenticator to view payroll insights.',
          })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="payroll-mfa-code">
              {t('payrollBridge:mfa.codeLabel', { defaultValue: 'Verification code' })}
            </Label>
            <Input
              id="payroll-mfa-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\s+/g, ''))}
              maxLength={10}
              placeholder={t('payrollBridge:mfa.codePlaceholder', { defaultValue: '123456' })}
            />
          </div>

          {error ? (
            <Alert variant="destructive">
              <AlertDescription>
                {t('payrollBridge:mfa.error', {
                  defaultValue: 'Verification failed. {{message}}',
                  message: parsedError ?? t('common:status.tryAgain', { defaultValue: 'Please try again.' }),
                })}
              </AlertDescription>
            </Alert>
          ) : null}

          <Button
            type="submit"
            className="w-full lg:w-auto"
            disabled={isPending || code.length < 6}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('payrollBridge:mfa.verifying', { defaultValue: 'Verifying…' })}
              </>
            ) : (
              t('payrollBridge:mfa.verify', { defaultValue: 'Verify access' })
            )}
          </Button>

          <p className="text-xs text-emerald-900 dark:text-emerald-100">
            {t('payrollBridge:mfa.help', {
              defaultValue: 'Need help? Ask an account owner to reset payroll MFA.',
            })}
          </p>
        </form>
      </CardContent>
    </Card>
  );
};

export default PayrollMfaGate;
